/**
 * Settings, Claude: how to add Noodle Studio to Claude as a connector (the address to paste),
 * and the connections the person has allowed, each of which can be taken back.
 * The list and the taking back go through the connector's own server addresses
 * (/oauth/connections, /oauth/disconnect), with the person's Firebase ID token.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Copy, Loader2, Plug } from 'lucide-react';
import { getCurrentIdToken } from '../utils/storage';
import { formatRelativeTime } from '../utils/workspaceLibrary';

interface ConnectionRow {
    id: string;
    name: string;
    connected_at: number;
    last_used_at: number;
}

/**
 * The address Claude is given: always https and on the internet, as Claude takes no other. The
 * app's own address is used only when it is one (the website); on this computer (the dev server,
 * the desktop app) it is a local http one, so the site's is shown. VITE_CONNECTOR_URL names it
 * when the connector has a host of its own.
 */
export const PUBLIC_CONNECTOR_URL = (import.meta.env.VITE_CONNECTOR_URL as string | undefined) || 'https://noodlestudio.ai/mcp';
const isHttps = (url: unknown): url is string => typeof url === 'string' && url.startsWith('https://');
const startAddress = () => (window.location.protocol === 'https:' ? `${window.location.origin}/mcp` : PUBLIC_CONNECTOR_URL);

/** "5 min ago", "just now" */
const when = (time: number) => { const text = formatRelativeTime(time); return text === 'Just now' ? 'just now' : text; };

const STEPS = [
    <>In Claude, open <strong>Settings</strong>, then <strong>Connectors</strong>, and choose <strong>Add custom connector</strong>.</>,
    <>Give it a name such as Noodle Studio, paste the address above, and add it.</>,
    <>Claude opens a Noodle Studio page. Check it is your account and choose <strong>Allow</strong>.</>,
];

export const ClaudeConnectorPanel: React.FC = () => {
    const [address, setAddress] = useState<string>(startAddress);
    const [rows, setRows] = useState<ConnectionRow[] | null>(null);
    // The connector's server did not answer from here: none behind this address yet
    const [unreachable, setUnreachable] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [removing, setRemoving] = useState<Set<string>>(new Set());

    const load = useCallback(async () => {
        setError(null);
        setUnreachable(false);
        let res: Response;
        let data: Record<string, any> | null;
        try {
            const token = await getCurrentIdToken();
            res = await fetch('/oauth/connections', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            data = (res.headers.get('content-type') ?? '').includes('application/json') ? await res.json().catch(() => null) : null;
        } catch {
            res = new Response(null, { status: 599 });
            data = null;
        }
        // Not the connector answering (a page, a password wall, a stand-in, nothing at all): it always names its address
        if (!data || (res.ok && typeof data.connector_url !== 'string')) { setRows([]); setUnreachable(true); return; }
        if (!res.ok) { setRows([]); setError(data.error || 'Your connections could not be read.'); return; }
        if (isHttps(data.connector_url)) setAddress(data.connector_url);
        setRows(Array.isArray(data.connections) ? data.connections : []);
    }, []);

    useEffect(() => { void load(); }, [load]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(address);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        } catch {
            setError('The address could not be copied. Select it and copy it by hand.');
        }
    };

    const disconnect = async (row: ConnectionRow) => {
        if (removing.has(row.id)) return;
        // Gone from the list at once; back if the server says no
        setRemoving(prev => new Set(prev).add(row.id));
        setRows(prev => prev?.filter(r => r.id !== row.id) ?? prev);
        try {
            const token = await getCurrentIdToken();
            const res = await fetch('/oauth/disconnect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ id: row.id }),
            });
            if (!res.ok) throw new Error();
        } catch {
            setRows(prev => (prev ? [...prev, row].sort((a, b) => b.connected_at - a.connected_at) : [row]));
            setError(`${row.name} could not be disconnected. Try again.`);
        } finally {
            setRemoving(prev => { const next = new Set(prev); next.delete(row.id); return next; });
        }
    };

    return (
        <div className="space-y-4" data-claude-connector>
            <section className="ns-card">
                <div className="flex items-center gap-3 mb-4">
                    <span className="ns-mark ns-mark-lavender" aria-hidden="true"><Plug size={20} /></span>
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-ink">Use Noodle Studio from Claude</h2>
                        <p className="text-sm text-ink-soft">Claude can read, create and organise workspaces, read and write Ideas cards, build and arrange nodes on your canvases with their prompts and references, look at their pictures, and read the comments left on them.</p>
                    </div>
                </div>

                <p className="ns-t-label mb-1.5">Connector address</p>
                <div className="flex items-center gap-2 mb-2">
                    <input className="ns-input flex-1 min-w-0 font-mono text-sm" value={address} readOnly aria-label="Connector address" onFocus={e => e.currentTarget.select()} />
                    <button type="button" className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat shrink-0" onClick={copy}>
                        {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
                {unreachable && (
                    <p className="ns-note mb-3" data-tone="warn" data-connector-unreachable>
                        <AlertTriangle aria-hidden="true" />
                        <span>The connector is not answering yet, so Claude cannot connect with this address for now. It works once the connector is live on the site.</span>
                    </p>
                )}
                <p className="ns-t-meta mb-4">It never generates or spends money: you press send on each node in the app. It sees only what your account can see. Nodes it deletes can be put back for 30 days, and it cannot delete workspaces, projects or folders.</p>

                <ol className="space-y-3">
                    {STEPS.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <span className="ns-mark ns-mark-lavender [--size:24px] text-xs font-semibold" aria-hidden="true">{i + 1}</span>
                            <span className="text-sm text-ink-soft">{step}</span>
                        </li>
                    ))}
                </ol>
            </section>

            <section className="ns-card" aria-labelledby="claude-connections">
                <h3 id="claude-connections" className="text-lg font-semibold text-ink mb-3">Connections</h3>
                {error && <p className="ns-note mb-3" data-tone="warn" role="alert"><AlertTriangle aria-hidden="true" /><span>{error}</span></p>}
                {rows === null ? (
                    <p className="flex items-center gap-2 text-sm text-ink-soft" role="status"><Loader2 size={16} className="animate-spin" aria-hidden="true" />Reading your connections</p>
                ) : unreachable ? (
                    <p className="text-sm text-ink-soft">Your connections cannot be listed until the connector is live.</p>
                ) : rows.length === 0 ? (
                    <p className="text-sm text-ink-soft">Nothing is connected yet.</p>
                ) : (
                    <ul className="space-y-2">
                        {rows.map(row => (
                            <li key={row.id} className="ns-well flex items-center justify-between gap-3 p-3" data-connection={row.id}>
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-ink truncate flex items-center gap-2"><span className="ns-tone-dot" data-tone="ok" />{row.name}</p>
                                    <p className="ns-t-meta">Connected {when(row.connected_at)}{row.last_used_at > row.connected_at ? `, last signed in again ${when(row.last_used_at)}` : ''}</p>
                                </div>
                                <button type="button" className="ns-btn ns-btn-sm ns-btn-danger ns-btn-flat shrink-0" onClick={() => disconnect(row)}>
                                    Disconnect
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default ClaudeConnectorPanel;
